import { User } from '@auth0/auth0-react'
import { Button, Dialog, DialogContent, DialogFooter, DialogTitle } from '@dfh-corp-sites/ui'
import { cn } from '@dfh-corp-sites/ui/utils'
import { addBusinessDays } from 'date-fns'
import React, { useCallback } from 'react'

import { Dispensary } from '../../../../graphql/camel'
import useSiteMetadata from '../../../../hooks/useSiteMetadata'
import useSiteSettings from '../../../../hooks/useSiteSettings'
import { removeDispensaryPrefixAndSlug } from '../../../../lib/dispensaries'
import { setPractitionerFormsState } from '../../../../lib/onboarding/practitioner-forms-state'
import { usePageUrl } from '../../../../lib/pages'
import { useLocationContext } from '../../../../lib/pages/hooks/useLocationContext'
import type { PractitionerSettings } from '../../../../lib/practitioner-settings/types'
import { publishEvent } from './CommissionsNotification'

const useIsExcludedPath = (excludedPathPatterns?: RegExp[]): boolean => {
  const location = useLocationContext()
  const path = removeDispensaryPrefixAndSlug(location?.pathname ?? '/')
  const patterns = [...(excludedPathPatterns ?? [])]
  return patterns.some((pattern) => pattern.test(path))
}

interface CommissionsModalProps {
  practitionerFormsAreNeeded: boolean
  showModal: boolean
  setShowModal: (showModal: boolean) => void
  reminderIsOverdue: boolean
  practitionerSettings?: PractitionerSettings['practitioner']
  excludedPathPatterns: RegExp[]
  notificationType: string
  className?: string
  user?: User
  dispensary?: Dispensary
}

const CommissionsModal: React.FC<CommissionsModalProps> = ({
  practitionerFormsAreNeeded,
  showModal,
  setShowModal,
  reminderIsOverdue,
  practitionerSettings,
  excludedPathPatterns,
  notificationType,
  user,
  dispensary,
}) => {
  const isExcludedPath = useIsExcludedPath(excludedPathPatterns)
  const { eftForm, taxIdForm } = useSiteSettings()
  const { achFormReceived, taxIdFormReceived } = practitionerSettings ?? {}
  const { navigate } = usePageUrl()
  const { commissionsNotificationDaysToHide } = useSiteMetadata() ?? {}
  const daysToHide = commissionsNotificationDaysToHide ?? 10

  const dismissModal = useCallback(() => {
    setShowModal(false)
    publishEvent({
      eventKey: 'commissionsNotificationDismissed',
      user,
      dispensary,
    })

    setPractitionerFormsState({
      lastEvent: 'toast_dismissed',
      notifyAfter: addBusinessDays(new Date(), daysToHide).toISOString(),
    })
  }, [setShowModal, user, dispensary, daysToHide])

  const navigateToRewardsOptOut = useCallback(() => {
    navigate('/account/settings/rewards-program')
    publishEvent({
      eventKey: 'commissionsNotificationNavigatedToOptOut',
      user,
      dispensary,
    })
    setPractitionerFormsState({
      lastEvent: 'toast_selected',
      notifyAfter: addBusinessDays(new Date(), daysToHide).toISOString(),
    })
  }, [navigate, user, dispensary, daysToHide])

  const navigateToPractitionerForms = useCallback(() => {
    navigate('/account/settings/documents')
    publishEvent({
      eventKey: 'commissionsNotificationNavigatedToSubmit',
      user,
      dispensary,
    })
    setPractitionerFormsState({
      lastEvent: 'toast_selected',
      notifyAfter: addBusinessDays(new Date(), daysToHide).toISOString(),
    })
  }, [navigate, user, dispensary, daysToHide])

  return (
    <Dialog
      open={
        practitionerFormsAreNeeded &&
        (notificationType === 'modal' || showModal) &&
        (!!eftForm || !!taxIdForm) &&
        !isExcludedPath &&
        reminderIsOverdue
      }
      onOpenChange={(open) => {
        if (!open) {
          dismissModal()
        }
      }}
      aria-describedby="scroll-dialog-description"
      aria-labelledby="scroll-dialog-title"
    >
      <DialogContent className="max-w-[min(670px,95vw)]" closeSize="lg">
        <div className={cn(['pt-8 pb-8 px-5', 'md:pt-10 md:px-10 md:pb-14'])}>
          <DialogTitle className="text-brand-navy text-balance text-xl md:text-2xl leading-[1.2] tracking-normal mr-8 mb-5">
            Your Dispensary will not generate commissions
          </DialogTitle>

          <p className="mb-6">Please submit the required documents to start earning commission.</p>
          <h3 className="font-medium">Required documents:</h3>
          <ul className="list-disc pl-5 mt-2">
            {!achFormReceived && !!eftForm && <li>{eftForm.label || 'EFT'} Form</li>}
            {!taxIdFormReceived && !!taxIdForm && (
              <li>{taxIdForm.label} Taxpayer Identification Form</li>
            )}
          </ul>
        </div>

        <DialogFooter className="w-full flex items-center sm:justify-between px-5 md:px-10 py-5 border-t border-brand-rule-grey">
          <Button
            variant="text"
            className={cn(['underline mt-2', 'md:mt-0'])}
            onClick={navigateToRewardsOptOut}
          >
            Opt out of commissions
          </Button>

          <Button size="sm" onClick={navigateToPractitionerForms}>
            Submit documents
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export default CommissionsModal
